import { Analytics } from '@cobliteam/analytics';
import type { Insight } from '@cobliteam/api-lib/src/__generated__/graphql';
import Badge from '@cobliteam/ui/components/Badge';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { endOfWeek, format, startOfWeek, subDays } from '@cobliteam/utils/date';
import { insightUtils } from '../..';

interface WeekInsightItemProps {
  insight: Insight;
  isSelected: boolean;
  onInsightClick: () => void;
  wasInsightSeen: boolean;
}

const WeekInsightItem = ({ insight, isSelected, onInsightClick, wasInsightSeen }: WeekInsightItemProps) => {
  if (!insight.impact) return null;

  const getCategoryIcon = (category?: string | null) => {
    if (!category) return 'lightbulb';
    const categoryLower = category.toLowerCase();
    if (categoryLower === 'safety' || categoryLower === 'security') {
      return 'shield-check';
    }
    return 'lightbulb';
  };

  return (
    <div
      className={cn(
        'group relative flex cursor-pointer flex-row items-center gap-3 rounded-lg border px-4 py-3 transition-all duration-200',
        isSelected
          ? 'border-primary-6 bg-primary-2 shadow-sm'
          : wasInsightSeen
            ? 'border-gray-4 bg-white hover:border-gray-5 hover:bg-gray-2'
            : 'border-primary-4 bg-primary-1 hover:border-primary-6 hover:bg-primary-2'
      )}
      onClick={onInsightClick}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-md transition-colors duration-200',
          isSelected ? 'bg-primary-3 text-primary' : wasInsightSeen ? 'bg-gray-3 text-gray-9' : 'bg-primary-3 text-primary'
        )}
      >
        <Icon name={getCategoryIcon(insight.category)} className='size-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <Badge color='primary' variant='dot' position='top-right' hide={isSelected || wasInsightSeen}>
          <Typography variant='text-sm-medium' className={cn('line-clamp-2', isSelected ? 'text-gray-12' : wasInsightSeen ? 'text-gray-11' : 'text-gray-12')}>
            {insight.impact}
          </Typography>
        </Badge>
      </div>
    </div>
  );
};

export interface WeekInsightsProps {
  insights: Insight[];
  selectedInsight?: Insight;
  onInsightClick: (insight: Insight) => void;
  readInsights?: { [id: string]: string };
}

const WeekInsights = ({ insights, selectedInsight, onInsightClick, readInsights = {} }: WeekInsightsProps) => {
  const { getInsightType } = insightUtils();
  const isInsightRead = (insightId: string) => {
    return Boolean(readInsights[insightId]);
  };

  const handleInsightClick = (insight: Insight) => {
    Analytics.track('Insight Modal List Item Clicked', { id: insight.id, type: getInsightType(insight), category: insight.category });
    onInsightClick(insight);
  };

  const renderInsight = (insight: Insight) => (
    <WeekInsightItem
      key={insight.id}
      insight={insight}
      isSelected={Boolean(selectedInsight && insight.id === selectedInsight.id)}
      onInsightClick={() => handleInsightClick(insight)}
      wasInsightSeen={isInsightRead(insight.id)}
    />
  );

  if (insights.length === 0) return null;

  const dayBeforeInsightGeneration = subDays(new Date(insights[0].createdAt), 1);
  const weekStart = startOfWeek(dayBeforeInsightGeneration);
  const weekEnd = endOfWeek(dayBeforeInsightGeneration);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-2'>
        <div className='h-px flex-1 bg-gray-4' />
        <Typography variant='text-sm-medium' color='secondary' className='shrink-0 px-2'>
          Semana {format(weekStart, 'dd/MM/yy')} - {format(weekEnd, 'dd/MM/yy')}
        </Typography>
        <div className='h-px flex-1 bg-gray-4' />
      </div>
      <div className='flex flex-col gap-2'>{insights?.map(renderInsight)}</div>
    </div>
  );
};

export default WeekInsights;
